import { StackScreenProps } from "@react-navigation/stack";
import { RegisterRootParamList } from "../../../../types";
import { Button, Page, Stack } from "@towersystems/roam-common-native";
import { useTranslation } from "../../../../utilities/i18n";
import { useAddItem } from "../../utils/sale-controller";
import Product from "../../../../utilities/data/model/Product";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import React from "react";
import { Colors, View } from "react-native-ui-lib";
import { AddCartNonPricedProductForm } from "../../components/AddCartNonPricedProductForm";
import { useFormContext } from "react-hook-form";
import { AddCartNonPricedProductFormValues } from "../../components/AddCartNonPricedProductForm/types";

export type AddNonPricedItemModalScreenProps = StackScreenProps<
  RegisterRootParamList,
  "AddNonPricedItemModalScreen"
>;

export const AddNonPricedItemModal = ({
  navigation,
}: AddNonPricedItemModalScreenProps & { product: Product }) => {
  const { t } = useTranslation("pos");
  const addItem = useAddItem({ navigation: navigation });
  const { handleSubmit, formState, setError, reset } =
    useFormContext<AddCartNonPricedProductFormValues>();

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const disabled =
    !isDirty ||
    isLoading ||
    isSubmitting ||
    isValid == false ||
    isSubmitSuccessful;

  const handleOnSubmit = React.useCallback(
    async ({
      productId,
      quantity,
      ...values
    }: AddCartNonPricedProductFormValues) => {
      try {
        await addItem(productId, {
          ...values,
          basePrice: parseFloat(values.price),
        });
        navigation.pop();
      } catch (error: any) {
        setError("root", {
          type: "serverError",
          message: error.message,
        });
      }
    },
    [addItem, reset]
  );

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.add_cart")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title={t("add_non_priced_item_title")}
      secondaryTitle={t("add_non_priced_item_subtitle")}
      footer={footerMarkup}
    >
      <Stack vertical>
        <AddCartNonPricedProductForm.NameFormInput />
        <AddCartNonPricedProductForm.PriceFormInput />
      </Stack>
    </Page>
  );
};

export const AddNonPricedItemModalScreen = (
  props: AddNonPricedItemModalScreenProps
) => {
  const [product, setProduct] = React.useState<Product | undefined>(undefined);
  const database = useDatabase();
  const { productId } = props.route.params;

  React.useEffect(() => {
    database.collections
      .get<Product>("products")
      .find(productId)
      .then((product) => {
        setProduct(product);
      })
      .catch(console.log);
  }, []);

  if (!product) {
    return <View />;
  }

  return (
    <AddCartNonPricedProductForm.Provider
      defaultFormValues={{
        productId: product.id,
        quantity: "1",
        price: "0",
        name: product.name,
      }}
    >
      <AddNonPricedItemModal product={product} {...props} />
    </AddCartNonPricedProductForm.Provider>
  );
};
